import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BBALoader from '../BBALoader';
import { Plus, Search, Clock, CheckCircle2, Hourglass, PlayCircle, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  listTaskTimeRequests,
  createTaskTimeRequest,
  setTaskTimeHours,
  setHoursAndApprove,
  respondToTaskTimeRequest,
  startTaskTimeRequest,
  completeTaskTimeRequest,
  denyTaskTimeRequest,
  deleteTaskTimeRequest,
  syncTaskTimeDeliverableLogs,
  findUnloggedTaskTimeRequests,
  computeTaskTimeBalance,
  fetchAccountTaskTimeAllowance,
  formatTaskTimeHours,
} from '../../services/taskTimeService';
import {
  TASK_TIME_FILTER_OPTIONS,
  TASK_TIME_STATUS,
  TASK_TIME_DEPARTMENTS,
  taskTimeDepartmentLabel,
  normalizeTaskTimeDepartment,
  taskTimeRequestHours,
} from '../../utils/taskTimeConstants';
import TaskTimeStatusBadge from '../taskTime/TaskTimeStatusBadge';
import TaskTimeHoursInput from '../taskTime/TaskTimeHoursInput';

function formatDate(value) {
  if (!value) return '—';
  if (typeof value.toDate === 'function') return value.toDate().toLocaleDateString();
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleDateString() : '—';
}

/**
 * Account-level Task Time tab (pooled across departments, filterable by department).
 * @param {{ accountId: string, active?: boolean, initialDepartment?: string }} props
 */
export default function AccountTaskTimeTab({
  accountId,
  active = true,
  initialDepartment = 'all',
}) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [allowance, setAllowance] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState(
    initialDepartment === 'all' ? 'all' : normalizeTaskTimeDepartment(initialDepartment)
  );
  const [actionError, setActionError] = useState('');

  // Hours modal: set/adjust the request's single time amount, optionally
  // approving in the same step when the client already agreed on a call.
  const [hoursModal, setHoursModal] = useState(null);
  const [hoursValue, setHoursValue] = useState('');
  const [crmNotes, setCrmNotes] = useState('');
  const [hoursApproveNow, setHoursApproveNow] = useState(false);
  const [saving, setSaving] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newDepartment, setNewDepartment] = useState(
    initialDepartment === 'all' ? 'technology' : normalizeTaskTimeDepartment(initialDepartment)
  );
  const [newHours, setNewHours] = useState('');
  const [createApproveNow, setCreateApproveNow] = useState(false);

  const userName =
    currentUser?.displayName || currentUser?.email?.split('@')[0] || 'CRM User';

  // One backfill attempt per account per mount, so a client whose deliverable
  // can't be resolved doesn't retry forever.
  const syncedAccountRef = useRef(null);

  const loadRequests = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      let [list, allowanceInfo] = await Promise.all([
        listTaskTimeRequests(accountId),
        fetchAccountTaskTimeAllowance(accountId),
      ]);

      // Tasks approved before deliverable logging existed still deduct from the
      // pool but never showed under Program Deliverables — reconcile them once,
      // then re-read so both views agree.
      if (syncedAccountRef.current !== accountId && findUnloggedTaskTimeRequests(list).length > 0) {
        syncedAccountRef.current = accountId;
        try {
          await syncTaskTimeDeliverableLogs(accountId);
          [list, allowanceInfo] = await Promise.all([
            listTaskTimeRequests(accountId),
            fetchAccountTaskTimeAllowance(accountId),
          ]);
        } catch (syncErr) {
          console.error('Task time deliverable sync failed:', syncErr);
        }
      }

      setRequests(list);
      setAllowance(allowanceInfo);
    } catch (e) {
      console.error(e);
      setRequests([]);
      setAllowance(null);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (!active || !accountId) return;
    loadRequests();
  }, [active, accountId, loadRequests]);

  const balance = useMemo(
    () =>
      computeTaskTimeBalance(requests, {
        allowanceHours: allowance?.allowanceHours ?? null,
        usedCallHours: allowance?.usedCallHours ?? 0,
        periodKey: allowance?.periodKey ?? null,
        periodStart: allowance?.periodStart ?? null,
        periodEnd: allowance?.periodEnd ?? null,
      }),
    [requests, allowance]
  );

  // Cap for the hours picker. A task that already deducts has its own hours
  // baked into `remainingHours`, so add them back or you couldn't re-pick the
  // value it's already set to.
  const hoursModalMax = useMemo(() => {
    if (!hoursModal || balance.remainingHours == null) return null;
    const own =
      [TASK_TIME_STATUS.APPROVED, TASK_TIME_STATUS.IN_PROGRESS].includes(hoursModal.status)
        ? taskTimeRequestHours(hoursModal) || 0
        : 0;
    return Math.round((balance.remainingHours + own) * 2) / 2;
  }, [hoursModal, balance.remainingHours]);

  const filtered = useMemo(() => {
    let rows = requests;
    if (departmentFilter !== 'all') {
      rows = rows.filter((r) => r.department === departmentFilter);
    }
    if (statusFilter !== 'all') {
      rows = rows.filter((r) => r.status === statusFilter);
    }
    const term = search.trim().toLowerCase();
    if (term) {
      rows = rows.filter((r) =>
        [r.title, r.description, r.requestedByName, r.crmNotes, taskTimeDepartmentLabel(r.department)]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(term))
      );
    }
    return rows;
  }, [requests, statusFilter, departmentFilter, search]);

  const hasFilters = statusFilter !== 'all' || departmentFilter !== 'all' || search.trim() !== '';

  const inputClass =
    'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white border-gray-300 text-gray-900 placeholder-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400';

  const runAction = async (fn) => {
    setActionError('');
    setSaving(true);
    try {
      await fn();
      await loadRequests();
    } catch (e) {
      console.error(e);
      setActionError(e.message || 'Action failed.');
    } finally {
      setSaving(false);
    }
  };

  const openHoursModal = (request, approveNow) => {
    setHoursModal(request);
    setHoursValue(taskTimeRequestHours(request) != null ? String(taskTimeRequestHours(request)) : '');
    setCrmNotes('');
    setHoursApproveNow(approveNow);
  };

  const closeHoursModal = () => {
    setHoursModal(null);
    setHoursValue('');
    setCrmNotes('');
    setHoursApproveNow(false);
  };

  const handleSaveHours = async (e) => {
    e.preventDefault();
    if (!hoursModal) return;
    const action = hoursApproveNow ? setHoursAndApprove : setTaskTimeHours;
    await runAction(async () => {
      await action(hoursModal.id, { hours: hoursValue, crmNotes, respondedByName: userName });
      closeHoursModal();
    });
  };

  /** Approve on the client's behalf; deducts from this period's pool. */
  const handleApproveForClient = (request) => {
    const hours = taskTimeRequestHours(request);
    if (hours == null) {
      openHoursModal(request, true);
      return;
    }
    if (
      !window.confirm(
        `Approve "${request.title}" on the client's behalf?\n\n${formatTaskTimeHours(hours)} will be deducted from their current period's implementation hours.`
      )
    )
      return;
    runAction(() =>
      respondToTaskTimeRequest(request.id, { approved: true, respondedByName: userName })
    );
  };

  const handleStart = (request) => {
    if (!window.confirm(`Start work on "${request.title}"?`)) return;
    runAction(() => startTaskTimeRequest(request.id));
  };

  const handleComplete = (request) => {
    if (!window.confirm(`Mark "${request.title}" complete?`)) return;
    runAction(() => completeTaskTimeRequest(request.id));
  };

  const handleDeny = (request) => {
    const notes = window.prompt('Optional note for denial:', request.crmNotes || '');
    if (notes === null) return;
    runAction(() => denyTaskTimeRequest(request.id, { crmNotes: notes }));
  };

  const handleDelete = (request) => {
    if (
      !window.confirm(
        `Delete "${request.title}"?\n\nThis permanently removes the row. Any hours it was holding go back to the client's pool.`
      )
    )
      return;
    runAction(() => deleteTaskTimeRequest(request.id));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    await runAction(async () => {
      const { id } = await createTaskTimeRequest({
        accountId,
        department: newDepartment,
        title: newTitle,
        description: newDescription,
        hours: newHours || undefined,
        requestedByName: userName,
      });
      if (newHours && createApproveNow) {
        await respondToTaskTimeRequest(id, { approved: true, respondedByName: userName });
      }
      setCreateModalOpen(false);
      setNewTitle('');
      setNewDescription('');
      setNewHours('');
      setCreateApproveNow(false);
    });
  };

  const metricCards = [
    {
      key: 'used',
      label: 'Hours used',
      value: formatTaskTimeHours(balance.deductedHours),
      sub:
        allowance?.allowanceHours != null
          ? `${formatTaskTimeHours(balance.remainingHours)} of ${formatTaskTimeHours(allowance.allowanceHours)} left`
          : null,
      Icon: Clock,
      iconClass: 'text-red-500 dark:text-red-400',
    },
    {
      key: 'requested',
      label: 'Requested',
      value: String(balance.requestedCount),
      sub: balance.requestedCount > 0 ? 'awaiting approval' : null,
      Icon: Hourglass,
      iconClass: 'text-amber-500 dark:text-amber-400',
    },
    {
      key: 'approved',
      label: 'Approved',
      value: String(balance.approvedCount),
      sub: allowance?.periodDisplay || null,
      Icon: PlayCircle,
      iconClass: 'text-blue-500 dark:text-blue-400',
    },
    {
      key: 'completed',
      label: 'Completed',
      value: String(balance.completedCount),
      sub:
        balance.usedCallHours > 0
          ? `${formatTaskTimeHours(balance.usedCallHours)} on calls`
          : null,
      Icon: CheckCircle2,
      iconClass: 'text-green-500 dark:text-green-400',
    },
  ];

  const selectClass =
    'px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metricCards.map(({ key, label, value, sub, Icon, iconClass }) => (
          <div key={key} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">{value}</div>
            <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 min-h-[14px]">
              {sub || ''}
            </div>
          </div>
        ))}
      </div>

      {actionError && (
        <p className="text-sm text-red-600 dark:text-red-400">{actionError}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks…"
            className={`${inputClass} pl-9`}
          />
        </div>
        <select
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          className={selectClass}
        >
          <option value="all">All departments</option>
          {TASK_TIME_DEPARTMENTS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={selectClass}
        >
          {TASK_TIME_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.value === 'all' ? 'All statuses' : opt.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setCreateModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium whitespace-nowrap"
        >
          <Plus className="w-4 h-4" />
          New task
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <BBALoader size={32} />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-center py-12 text-gray-500 dark:text-gray-400">
          No task time requests{hasFilters ? ' for this filter' : ''}.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="px-4 py-3 font-medium">Task</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Time</th>
                <th className="px-4 py-3 font-medium">Requested</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((req) => (
                <tr
                  key={req.id}
                  className="border-t border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white">{req.title}</p>
                    {req.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                        {req.description}
                      </p>
                    )}
                    {Array.isArray(req.attachments) && req.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {req.attachments.map((f) => (
                          <a
                            key={f.url}
                            href={f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            📎 <span className="max-w-[140px] truncate">{f.name || 'Attachment'}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    {req.crmNotes && (
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">CRM: {req.crmNotes}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {taskTimeDepartmentLabel(req.department)}
                  </td>
                  <td className="px-4 py-3">
                    <TaskTimeStatusBadge status={req.status} />
                    {req.respondedSource === 'crm' && req.status !== TASK_TIME_STATUS.DENIED && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                        Approved internally
                        {req.respondedByName || req.clientRespondedByName
                          ? ` · ${req.respondedByName || req.clientRespondedByName}`
                          : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    <button
                      type="button"
                      onClick={() => openHoursModal(req, false)}
                      disabled={
                        saving ||
                        [TASK_TIME_STATUS.COMPLETED, TASK_TIME_STATUS.DENIED].includes(req.status)
                      }
                      title="Edit time"
                      className="px-1.5 py-0.5 -mx-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:hover:bg-transparent disabled:cursor-default"
                    >
                      {taskTimeRequestHours(req) != null
                        ? formatTaskTimeHours(taskTimeRequestHours(req))
                        : '— set'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    <div>{formatDate(req.requestedAt)}</div>
                    <div className="text-xs">{req.requestedByName || '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {req.status === TASK_TIME_STATUS.REQUESTED && (
                        <button
                          type="button"
                          onClick={() => handleApproveForClient(req)}
                          disabled={saving}
                          title="Approve and deduct from this period's implementation hours"
                          className="px-2 py-1 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                        >
                          Approve
                        </button>
                      )}
                      {req.status === TASK_TIME_STATUS.APPROVED && (
                        <button
                          type="button"
                          onClick={() => handleStart(req)}
                          disabled={saving}
                          className="px-2 py-1 text-xs font-medium rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
                        >
                          Start
                        </button>
                      )}
                      {[TASK_TIME_STATUS.APPROVED, TASK_TIME_STATUS.IN_PROGRESS].includes(
                        req.status
                      ) && (
                        <button
                          type="button"
                          onClick={() => handleComplete(req)}
                          disabled={saving}
                          className="px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                          Complete
                        </button>
                      )}
                      {[
                        TASK_TIME_STATUS.REQUESTED,
                        TASK_TIME_STATUS.APPROVED,
                        TASK_TIME_STATUS.IN_PROGRESS,
                      ].includes(req.status) && (
                        <button
                          type="button"
                          onClick={() => handleDeny(req)}
                          disabled={saving}
                          className="px-2 py-1 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
                        >
                          Deny
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(req)}
                        disabled={saving}
                        title="Delete this row"
                        aria-label={`Delete ${req.title}`}
                        className="px-2 py-1 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200 dark:hover:bg-red-900/20 dark:hover:text-red-400 dark:hover:border-red-800 disabled:opacity-60"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hoursModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {hoursModal.status === TASK_TIME_STATUS.REQUESTED ? 'Set time' : 'Adjust time'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {hoursModal.title} · {taskTimeDepartmentLabel(hoursModal.department)}
            </p>
            {balance.remainingHours != null && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Client has {formatTaskTimeHours(balance.remainingHours)} remaining this period.
              </p>
            )}
            <form onSubmit={handleSaveHours} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time
                </label>
                <TaskTimeHoursInput
                  value={hoursValue}
                  onChange={setHoursValue}
                  className="w-full"
                  maxHours={hoursModalMax}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CRM notes (optional)
                </label>
                <textarea
                  rows={2}
                  value={crmNotes}
                  onChange={(e) => setCrmNotes(e.target.value)}
                  className={inputClass}
                  placeholder="Note shown to the client on this task"
                />
              </div>
              {hoursModal.status === TASK_TIME_STATUS.REQUESTED && (
                <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hoursApproveNow}
                    onChange={(e) => setHoursApproveNow(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500"
                  />
                  <span>
                    Approve on our end (client already agreed)
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Deducts the time from this period's implementation hours now.
                    </span>
                  </span>
                </label>
              )}
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={closeHoursModal}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !hoursValue}
                  className={`px-4 py-2 text-sm rounded-lg text-white disabled:opacity-60 ${
                    hoursApproveNow ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {saving ? 'Saving…' : hoursApproveNow ? 'Approve & deduct' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {createModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create task for client</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department
                </label>
                <select
                  value={newDepartment}
                  onChange={(e) => setNewDepartment(e.target.value)}
                  className={inputClass}
                >
                  {TASK_TIME_DEPARTMENTS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time{' '}
                  <span className="font-normal text-gray-500 dark:text-gray-400">
                    (optional — {createApproveNow ? 'deducted on create' : 'leaves the task as requested'})
                  </span>
                </label>
                <TaskTimeHoursInput
                  value={newHours}
                  onChange={setNewHours}
                  className="w-full"
                  maxHours={balance.remainingHours}
                />
                {balance.remainingHours != null && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formatTaskTimeHours(balance.remainingHours)} remaining this period
                    {allowance?.periodDisplay ? ` (${allowance.periodDisplay})` : ''}.
                  </p>
                )}
              </div>
              <label
                className={`flex items-start gap-2 text-sm cursor-pointer ${
                  newHours
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                <input
                  type="checkbox"
                  checked={createApproveNow}
                  disabled={!newHours}
                  onChange={(e) => setCreateApproveNow(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500"
                />
                <span>
                  Approve on our end (client already agreed)
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Deducts the time from this period's implementation hours now.
                  </span>
                </span>
              </label>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCreateModalOpen(false);
                    setCreateApproveNow(false);
                  }}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-4 py-2 text-sm rounded-lg text-white disabled:opacity-60 ${
                    createApproveNow && newHours
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {saving ? 'Creating…' : createApproveNow && newHours ? 'Create & approve' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
